import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  X,
  Loader2,
  Trophy,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Star,
  Crown,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const RANK_COLORS = [
  "text-amber-600 bg-amber-50 border-amber-200", // 1st (gold)
  "text-slate-700 bg-slate-100 border-slate-300", // 2nd (silver)
  "text-orange-700 bg-orange-50 border-orange-200", // 3rd (bronze)
  "text-slate-500 bg-slate-50 border-slate-200", // others
  "text-slate-500 bg-slate-50 border-slate-200",
];

function scoreColor(score) {
  const s = Number(score) || 0;
  if (s >= 8) return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (s >= 6) return "text-blue-700 bg-blue-50 border-blue-200";
  if (s >= 4) return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-rose-700 bg-rose-50 border-rose-200";
}

function initials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

const CandidateComparisonModal = ({
  jobId,
  candidateIds,
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  // Stable string key so React's dep-array doesn't fire on each parent re-render
  // (the array reference can change even when the contents are identical).
  const idsKey = (candidateIds || []).join(",");

  useEffect(() => {
    if (!open || !jobId || !idsKey) return;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    axios
      .post(
        `${API}/jobs/${jobId}/candidates/compare`,
        { candidate_ids: idsKey.split(",") },
        { signal: controller.signal, timeout: 180000 },
      )
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError("");
      })
      .catch((err) => {
        if (cancelled || axios.isCancel?.(err) || err?.code === "ERR_CANCELED") return;
        const msg =
          err?.response?.data?.detail ||
          "Não foi possível gerar a comparação. Tente novamente.";
        setError(msg);
        toast.error(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, jobId, idsKey]);

  const candidatesById = useMemo(() => {
    const m = {};
    (data?.candidates || []).forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [data]);

  const orderedCandidates = data?.candidates || [];
  const winnerId = data?.comparison?.final?.winner_candidate_id;
  const ranking = data?.comparison?.final?.ranking || [];
  const rankByCandidate = useMemo(() => {
    const m = {};
    ranking.forEach((rid, i) => (m[rid] = i + 1));
    return m;
  }, [ranking]);

  const handleRetry = () => {
    if (!jobId || !candidateIds?.length) return;
    setLoading(true);
    setError("");
    setData(null);
    axios
      .post(
        `${API}/jobs/${jobId}/candidates/compare`,
        { candidate_ids: candidateIds },
        { timeout: 180000 },
      )
      .then((res) => {
        setData(res.data);
        setError("");
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err?.code === "ERR_CANCELED") return;
        const msg =
          err?.response?.data?.detail ||
          "Não foi possível gerar a comparação. Tente novamente.";
        setError(msg);
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="candidate-comparison-modal"
        className="max-w-[1280px] w-[96vw] h-[92vh] p-0 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-violet-50 via-white to-blue-50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-violet-500 to-blue-500 text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="heading text-lg font-bold text-slate-900 truncate">
                Comparação de candidatos com IA
              </h2>
              <p className="text-xs text-slate-600 truncate">
                {data?.job?.title || "Carregando vaga..."}
                {data?.candidates?.length
                  ? ` · ${data.candidates.length} candidatos comparados`
                  : ""}
              </p>
            </div>
          </div>
          <button
            data-testid="close-comparison-modal"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-50/40">
          {loading && (
            <div
              data-testid="comparison-loading"
              className="h-full flex flex-col items-center justify-center gap-3 py-24"
            >
              <Loader2 className="w-8 h-8 text-brand-blue animate-spin" />
              <div className="text-sm font-semibold text-slate-700">
                A IA está comparando os candidatos…
              </div>
              <p className="text-xs text-slate-500 max-w-md text-center">
                Estamos analisando currículos, pontos fortes, lacunas e produzindo a matriz
                comparativa. Isso pode levar de 10 a 30 segundos.
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="h-full flex flex-col items-center justify-center gap-3 py-24 px-6">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
              <div className="text-sm font-semibold text-slate-900">
                Não foi possível gerar a comparação
              </div>
              <p className="text-xs text-slate-600 max-w-md text-center">{error}</p>
              <Button
                data-testid="comparison-retry"
                onClick={handleRetry}
                size="sm"
                className="bg-brand-blue hover:bg-brand-blue-dark text-white"
              >
                Tentar novamente
              </Button>
            </div>
          )}

          {!loading && !error && data && (
            <div className="p-6 space-y-6">
              {/* Final recommendation banner */}
              <div
                data-testid="comparison-final-recommendation"
                className="bg-gradient-to-r from-emerald-50 via-emerald-100/40 to-emerald-50 border border-emerald-300 rounded-lg p-5 flex items-start gap-4 shadow-sm"
              >
                <div className="shrink-0 w-12 h-12 rounded-full bg-amber-400 text-white flex items-center justify-center shadow-md">
                  <Crown className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-emerald-700 mb-1">
                    Recomendação final da IA
                  </div>
                  <div className="text-lg font-bold text-slate-900 leading-tight">
                    {candidatesById[winnerId]?.name || "—"}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed mt-2">
                    {data.comparison?.final?.justification ||
                      "Sem justificativa disponível."}
                  </p>
                  {ranking.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">
                        Ranking IA:
                      </span>
                      {ranking.map((rid, i) => {
                        const cand = candidatesById[rid];
                        if (!cand) return null;
                        return (
                          <span
                            key={rid}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${RANK_COLORS[Math.min(i, 3)]}`}
                          >
                            <span className="tabular-nums font-bold">#{i + 1}</span>
                            <span className="truncate max-w-[140px]">{cand.name}</span>
                            {i === 0 && <Star className="w-3 h-3 fill-current" />}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Candidate cards header row */}
              <div
                data-testid="comparison-headers"
                className={`grid gap-4`}
                style={{
                  gridTemplateColumns: `minmax(200px, 1.2fr) repeat(${orderedCandidates.length}, minmax(0, 1fr))`,
                }}
              >
                <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 self-end pb-2">
                  Critério / Candidato
                </div>
                {orderedCandidates.map((c) => {
                  const isWinner = c.id === winnerId;
                  const r = rankByCandidate[c.id];
                  return (
                    <div
                      key={c.id}
                      data-testid={`comparison-header-${c.id}`}
                      className={`relative bg-white border rounded-lg p-3 ${isWinner ? "border-emerald-300 ring-2 ring-emerald-200 shadow-md" : "border-slate-200"}`}
                    >
                      {isWinner && (
                        <div className="absolute -top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-500 text-white shadow">
                          Recomendado
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-md bg-brand-blue text-white flex items-center justify-center text-sm font-bold">
                          {initials(c.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            {r && (
                              <span
                                className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded ${RANK_COLORS[Math.min(r - 1, 3)]}`}
                              >
                                #{r}
                              </span>
                            )}
                            <div className="text-sm font-bold text-slate-900 truncate">
                              {c.name || "(sem nome)"}
                            </div>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Score IA: <span className="font-bold tabular-nums">{c.score}</span>/100
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {c.experience_years > 0
                              ? `${c.experience_years} anos de experiência`
                              : "experiência não informada"}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-candidate verdicts */}
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: `minmax(200px, 1.2fr) repeat(${orderedCandidates.length}, minmax(0, 1fr))`,
                }}
              >
                <div className="text-sm font-semibold text-slate-800 self-start pt-2">
                  Veredito relativo
                </div>
                {orderedCandidates.map((c) => {
                  const pc = data.comparison?.per_candidate?.find(
                    (p) => p.candidate_id === c.id
                  );
                  return (
                    <div
                      key={`verdict-${c.id}`}
                      data-testid={`comparison-verdict-${c.id}`}
                      className="bg-white border border-slate-200 rounded-lg p-3 space-y-3"
                    >
                      <p className="text-sm text-slate-800 leading-snug italic">
                        {pc?.verdict || (
                          <span className="text-slate-400 not-italic">
                            Sem veredito.
                          </span>
                        )}
                      </p>
                      {pc?.relative_strengths?.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Onde se destaca
                          </div>
                          <ul className="space-y-0.5">
                            {pc.relative_strengths.map((s, i) => (
                              <li
                                key={`${c.id}-s-${i}`}
                                className="text-xs text-slate-700 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[6px] before:w-1 before:h-1 before:rounded-full before:bg-emerald-500"
                              >
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {pc?.relative_concerns?.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Pontos de atenção
                          </div>
                          <ul className="space-y-0.5">
                            {pc.relative_concerns.map((g, i) => (
                              <li
                                key={`${c.id}-g-${i}`}
                                className="text-xs text-slate-700 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[6px] before:w-1 before:h-1 before:rounded-full before:bg-amber-500"
                              >
                                {g}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Criteria matrix */}
              {data.comparison?.criteria?.length > 0 && (
                <div
                  data-testid="comparison-criteria-matrix"
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-600" />
                      Matriz comparativa
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Notas de 0 a 10 atribuídas pela IA em cada critério.
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    {data.comparison.criteria.map((row, ri) => (
                      <div
                        key={`row-${ri}`}
                        data-testid={`criteria-row-${ri}`}
                        className="grid gap-4 items-start"
                        style={{
                          gridTemplateColumns: `minmax(200px, 1.2fr) repeat(${orderedCandidates.length}, minmax(0, 1fr))`,
                        }}
                      >
                        <div className="text-sm font-semibold text-slate-800 pt-1">
                          {row.name}
                        </div>
                        {orderedCandidates.map((c) => {
                          const sc = row.scores?.find(
                            (s) => s.candidate_id === c.id
                          );
                          const score = sc?.score ?? 0;
                          return (
                            <div
                              key={`${ri}-${c.id}`}
                              className={`p-2.5 rounded-md border ${scoreColor(score)} space-y-1`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">
                                  {c.name?.split(/\s+/)[0] || "—"}
                                </span>
                                <span className="text-base font-extrabold tabular-nums">
                                  {score}<span className="text-[10px] font-medium opacity-60">/10</span>
                                </span>
                              </div>
                              {sc?.comment && (
                                <p className="text-[11px] leading-snug opacity-90">
                                  {sc.comment}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-between shrink-0">
          <div className="text-xs text-slate-500">
            Análise gerada por Claude Sonnet 4.5 · Use como apoio, não como decisão única.
          </div>
          <div className="flex items-center gap-2">
            {data?.id && (
              <Button
                data-testid="comparison-export-pdf"
                onClick={() => {
                  window.open(`/comparacao/${data.id}/imprimir`, "_blank");
                }}
                variant="outline"
                size="sm"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
              >
                <Printer className="w-3.5 h-3.5 mr-1.5" />
                Exportar PDF
              </Button>
            )}
            <Button
              data-testid="comparison-close-footer"
              onClick={onClose}
              variant="outline"
              size="sm"
            >
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateComparisonModal;
